import { useEffect, useState } from 'react'
import { api } from '../../api/client'
import type { FieldValue } from '../../api/types'

export default function FieldEditor({ cid, field, saved }: { cid: string; field: FieldValue; saved: () => Promise<void> }) {
  const [value, setValue] = useState(field.value ?? '')
  const [status, setStatus] = useState(field.status === 'open' ? 'follow_up' : field.status)
  const [reason, setReason] = useState('')
  const [resolve, setResolve] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [history, setHistory] = useState<Awaited<ReturnType<typeof api.fieldHistory>>>([])
  // Keep the version captured on opening: polling must not hide concurrent edits.
  const [version] = useState(field.version)
  useEffect(() => { let active = true; api.fieldHistory(cid, field.field_id).then(h => { if (active) setHistory(h) }).catch(e => { if (active) setError(e.message) }); return () => { active = false } }, [cid, field.field_id])
  async function submit(event: React.FormEvent) {
    event.preventDefault(); if (!version) return; setBusy(true); setError('')
    try {
      await api.editField(cid, field.field_id, { value, status, reason, expected_version: version, resolve_conflicts: resolve })
      await saved()
    } catch (e) { setError((e as Error).message) }
    finally { setBusy(false) }
  }
  return <details><summary>Review or correct this answer</summary>
    {error && <p className="notice crit" role="alert">{error}</p>}
    <form onSubmit={submit}>
      <label>Status <select value={status} onChange={e => setStatus(e.target.value as typeof status)}>
        {['answered','unknown','declined','not_applicable','follow_up'].map(s => <option key={s} value={s}>{s.replaceAll('_',' ')}</option>)}
      </select></label>
      <label>Value <textarea value={value} onChange={e => setValue(e.target.value)} maxLength={20000} /></label>
      <label>Reason for correction <textarea required value={reason} onChange={e => setReason(e.target.value)} maxLength={2000} /></label>
      {field.has_conflict && <label><input type="checkbox" checked={resolve} onChange={e => setResolve(e.target.checked)} /> Resolve the recorded conflicts using this reviewed answer</label>}
      <p className="muted">The original quote and source remain attached. Confidence is recorded as analyst-reviewed.</p>
      <button className="btn secondary small" disabled={busy || !version}>Save correction</button>
    </form>
    <h3>Edit history</h3>
    {!history.length && <p>No analyst edits yet.</p>}
    {history.map(h => <details key={h.id}><summary>{h.actor} · {new Date(h.created_at).toLocaleString()}</summary><p>{h.reason}</p><pre style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify({ before: h.before, after: h.after }, null, 2)}</pre></details>)}
  </details>
}
